import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { find } from 'lodash';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components/macro';
import { WxButton, WxDialog, WxText } from '../components';
import { WImage } from '../components/ui/image/Image';
import useBankroll from '../hooks/api/queries/useBankroll';
import useOrderLight from '../hooks/api/queries/useOrderLight';
import { useAddressContext } from '../hooks/context/useAddressContext';
import { useCartContext } from '../hooks/context/useCartContext';
import { __ } from '../i18n/l10n';
import { breakpoint } from '../styles/constants';
import { SLUG_ORDER } from '../styles/slugs';
import { appendCommitHash } from '../utils/appendHash';

interface OrderState {
  orderId: string;
}

export const Confirmation = () => {
  const { accountAddress } = useAddressContext();
  const { orderErrors, setOrderErrors } = useCartContext();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as OrderState;

  const orderId = state?.orderId;

  const { data, error } = useOrderLight(parseInt(orderId ?? ''));
  const { data: bankroll } = useBankroll();

  useEffect(() => {
    if (bankroll && (window as any).updateMiles)
      (window as any).updateMiles(bankroll);
  }, [bankroll]);

  useEffect(() => {
    if (
      data &&
      find(data.orderItems, (item) => item.dematerializedType === 'FREEBET') &&
      (window as any).updateFreebets
    ) {
      const freebetsAmountArray = data.orderItems
        .filter((item) => item.dematerializedType === 'FREEBET')
        .map((item) =>
          parseInt(item.externalReferences[0].externalReference.split('-')[1]),
        );
      (window as any).updateFreebets(freebetsAmountArray[0]);
    }
  }, [data]);

  if (error || orderId === undefined) {
    return null;
  }

  return (
    <Wrapper>
      <WxDialog
        title={__('IMPORTANT')}
        open={orderErrors.length > 0}
        setOpen={(_: boolean) => setOrderErrors([])}
        content={__(
          'Some products could not be ordered due to %1$s. \n You can find them in your cart.',
          [orderErrors?.[0]?.message ?? ''],
        )}
        onClose={() => navigate(appendCommitHash(`/`))}
        noCancel
      />

      <CheckIcon icon={faCircleCheck} />
      <DivText>
        <WxText
          text={`${__('Thank you')}, ${accountAddress?.firstname}`}
          bold
          darkMode
          align="center"
        />
        <WxText
          text={__('We have received your order')}
          darkMode
          align="center"
        />
        <WxText
          text={__('You will receive a confirmation e-mail shortly')}
          darkMode
          align="center"
        />
      </DivText>
      <MinWidthDivButton>
        <Link to={appendCommitHash('/')}>
          <WxButton
            label={__('Back to the shop')}
            onClick={() => {}}
            type={'neutral'}
            darkMode
            expand
            noArrow
          />
        </Link>
      </MinWidthDivButton>
      <ImagesUl>
        {data?.orderItems?.map((item) => (
          <li key={`order-item-${item.id}`} style={{ maxWidth: 250 }}>
            <WImage
              image={
                item.localizedVariantImage ??
                item.defaultVariantImage ??
                item.localizedProductImage ??
                item.defaultProductImage
              }
              background
            />
          </li>
        ))}
      </ImagesUl>
      <MinWidthDivButton>
        <Link to={appendCommitHash(`/${SLUG_ORDER}/${state.orderId}`)}>
          <WxButton
            label={__('View order')}
            onClick={() => {}}
            type={'neutral'}
            darkMode
            expand
            noArrow
          />
        </Link>
      </MinWidthDivButton>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 2.5rem;
`;

const CheckIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.green};
  font-size: 1.875rem;
  margin-bottom: 1.25rem;
  margin-top: 5rem;

  @media (min-width: ${breakpoint}) {
    font-size: 5rem;
  }
`;

const DivText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9575rem;
  margin-bottom: 1.25rem;

  @media (min-width: ${breakpoint}) {
    margin-bottom: 2.5rem;
  }
`;

const ImagesUl = styled.ul`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  margin-block: 2.5rem 1.25rem;

  > li {
    max-width: calc(50% - 0.3125rem);
    min-width: 150px;

    @media (min-width: ${breakpoint}) {
      max-width: calc((100% -1.25rem) / 3);
    }
  }

  @media (min-width: ${breakpoint}) {
    margin-block: 3.75rem 2.5rem;
  }
`;

const MinWidthDivButton = styled.div`
  min-width: 197px;
  margin-inline: auto;
  > * {
    width: 100%;
  }
`;
